import { CreditCardCommandService, type RegisterInvoicePaymentInput, type RegisterInvoicePaymentResult } from '@/lib/creditCardCommandService';
import { CreditCardPurchaseIntentController, type CardIntentStorage } from '@/lib/creditCardPurchaseIntent';

type PaymentInput = Omit<RegisterInvoicePaymentInput, 'idempotencyKey' | 'effectiveAt'>;

export function createCreditCardPaymentIntentService(dependencies: {
  register: (input: RegisterInvoicePaymentInput) => Promise<RegisterInvoicePaymentResult>;
  storage: () => CardIntentStorage;
}) {
  const controllers = new Map<string, CreditCardPurchaseIntentController<RegisterInvoicePaymentResult>>();
  const completed = new WeakMap<Promise<RegisterInvoicePaymentResult>, Promise<RegisterInvoicePaymentResult>>();

  return {
    register(userId: string, input: PaymentInput): Promise<RegisterInvoicePaymentResult> {
      if (!userId || !input.invoiceId) return Promise.reject(new Error('card_payment_identity_required'));
      // Reuse the durable intent primitive, with a separate payment scope. Only
      // the opaque key and fingerprint persist, never financial payloads.
      const scope = `invoice-payment:${userId}:${input.invoiceId}`;
      let controller = controllers.get(scope);
      if (!controller) {
        controller = new CreditCardPurchaseIntentController(scope, dependencies.storage());
        controllers.set(scope, controller);
      }
      const active = controller;
      const semantic: PaymentInput = {
        invoiceId: input.invoiceId, amount: Math.round(input.amount * 100) / 100,
        accountId: input.accountId ?? null, paymentDate: input.paymentDate,
        notes: input.notes?.trim() || null,
      };
      const request = active.run(semantic, async intent => {
        const result = await dependencies.register({ ...semantic, effectiveAt: null, idempotencyKey: intent.idempotencyKey });
        if (!result?.payment?.id || result.invoice?.id !== semantic.invoiceId ||
          result.payment.invoice_id !== semantic.invoiceId || result.payment.payment_date !== semantic.paymentDate ||
          Number(result.payment.amount) !== semantic.amount ||
          result.payment.idempotency_key !== intent.idempotencyKey || !result.payment.effective_at) {
          throw new Error('card_payment_result_unconfirmed');
        }
        return result;
      });
      const existing = completed.get(request);
      if (existing) return existing;
      const result = request.then(value => {
        try {
          if (active.complete() && controllers.get(scope) === active) controllers.delete(scope);
        } catch { /* A cleanup failure must not turn a confirmed payment into an error. */ }
        return value;
      }, error => {
        const cause = error?.cause as { code?: string; message?: string } | undefined;
        const rollback = typeof cause?.code === 'string' && /^[0-9A-Z]{5}$/.test(cause.code) &&
          !/idempotency.*conflict/i.test(cause.message ?? '');
        try {
          if (active.resolveFailure(rollback) && controllers.get(scope) === active) controllers.delete(scope);
        } catch { /* Preserve an uncertain intent until its result is recovered. */ }
        throw error;
      });
      completed.set(request, result);
      return result;
    },
  };
}

export const CreditCardPaymentIntentService = createCreditCardPaymentIntentService({
  register: input => CreditCardCommandService.registerInvoicePayment(input),
  storage: () => {
    if (typeof window === 'undefined') throw new Error('card_payment_durable_storage_unavailable');
    return window.sessionStorage;
  },
});
